<script setup>
import { formatCurrency, formatNumber, formatPercent } from '../../Utils/numberFormat.js'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useRequestStatistics } from '../../Composables/useRequestStatistics'
import { useHttpErrorsHandler } from '../../Composables/httpErrorsHandler'
import { useLocalStorage } from '../../Composables/useLocalStorage'
import FormMonthPicker from '../../Components/FormMonthPicker.vue'
import StatChart from '../../Components/StatChart.vue'
import ChartSkeleton from '../../Components/ChartSkeleton.vue'
import CallStatistics from '../../Components/CallStatistics.vue'
import RevenueStatistics from '../../Components/RevenueStatistics.vue'
import { useAuthUserStore } from '../../Store/AuthUserStore.js'
import { serviceColor } from '../../Constants/serviceColors.js'

const { httpErrorsHandler } = useHttpErrorsHandler()
const stats = useRequestStatistics({ onError: httpErrorsHandler })
const $toast = useToast()

const TOP_N = 10
const COLORS = {
    appointment: '#f7b84b',
    won: '#0ab39c',
    requests: '#212529', // demandes (anthracite : le "total" neutre, le vert des gagnés ressort dessus)
    creations: '#299cdb', // fiches de travail (= couleur "info" du KPI Fiches)
    rate: '#3577f1', // courbe du taux (= couleur "secondary" du KPI Taux de transformation)
}

// Formatage des nombres avec séparateur de milliers (format FR : 3 082)
const formatAmount = (value) => formatCurrency(value, 'EUR', { maximumFractionDigits: 0 })

// Textes d'aide réutilisés sur les cartes (icône i)
const CONVERSION_HELP = "Gagnés : demandes encore gagnées aujourd'hui (gains annulés exclus), comptées au mois où la demande a été gagnée et attribuées au vendeur. RDV programmés : demandes ayant atteint le stade RDV, comptées au mois de la 1re prise de RDV. Chaque demande compte une seule fois."
const FICHES_HELP = 'Nombre de fiches de travail générées, comptées au mois de génération de la fiche.'
const RESPONDED_HELP = "Demandes actuellement au statut « répondu », comptées au mois de leur création. Contrairement aux RDV et aux gagnés, le snapshot ne conserve pas la date de passage à ce statut : c'est donc une photo de l'état présent, pas un flux."
const REVENUE_HELP = "Somme des prix saisis sur les demandes encore gagnées, sur le même périmètre que le compteur « Gagnés (nets) ». Le prix n'est pas renseigné sur toutes les demandes : ce montant est donc un plancher."
const SERVICES_HELP = "Prestations des demandes créées sur la période, réparties par centre. Une demande ne porte qu'une prestation ; celles sans prestation renseignée sont écartées. Au-delà des 6 plus fréquentes, le reste est regroupé sous « Autres »."
const CONTACTS_HELP = 'Contacts créés sur la période, comptés au mois de leur création. Un contact peut exister sans demande : ce compteur ne vient pas du même périmètre que les demandes.'

// Onglet affiché (Demandes / Appels / Objectifs), mémorisé pour le prochain passage sur la page
const TAB_STORAGE_KEY = 'statistics-tab'
const TABS = ['requests', 'calls', 'objectives']
const readStoredTab = () => {
    try {
        const stored = localStorage.getItem(TAB_STORAGE_KEY)
        return TABS.includes(stored) ? stored : 'requests'
    } catch {
        return 'requests'
    }
}
const activeTab = ref(readStoredTab())

// Quatre filtres sur les demandes, trois sur les appels (pas de filtre utilisateur) : ils se
// partagent toute la largeur dans les deux cas
const filterColClass = computed(() => activeTab.value === 'requests' ? 'col-xl-3 col-md-6' : 'col-xl-4 col-md-4')

// Instance sans aucun appel (ex. RS-Tronic, sans RingOver) : pas d'onglet Appels
const authUserStore = useAuthUserStore()
const hasCalls = computed(() => authUserStore.authUser?.has_calls !== false)
watch(hasCalls, (value) => {
    if (!value && activeTab.value === 'calls') {
        activeTab.value = 'requests'
    }
}, { immediate: true })
watch(activeTab, (tab) => {
    try {
        localStorage.setItem(TAB_STORAGE_KEY, tab)
    } catch {
        // stockage indisponible (navigation privée) : l'onglet n'est simplement pas mémorisé
    }

    if (tab === 'requests' && requestsStale) {
        applyFilters()
    }
})
// Les filtres de période ne sont connus qu'après le premier chargement de la page
const ready = ref(false)

const selectedCenter = ref(null)
const selectedUser = ref(null)
const from = ref(null)
const to = ref(null)

const centerOptions = ref([])
const userOptions = ref([])

const buildOptionsIfNeeded = () => {
    if (centerOptions.value.length === 0) {
        const centers = {}
        stats.centerCreations.value.rows.forEach((row) => {
            if (row.center_id) centers[row.center_id] = row.center_name || `#${row.center_id}`
        })
        stats.userStatusBreakdown.value.rows.forEach((row) => {
            if (row.center_id) centers[row.center_id] = row.center_name || `#${row.center_id}`
        })
        centerOptions.value = Object.entries(centers)
            .map(([id, name]) => ({ id: Number(id), name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    if (userOptions.value.length === 0) {
        const users = {}
        stats.userStatusBreakdown.value.rows.forEach((row) => {
            if (row.user_id) users[row.user_id] = row.user_name || `#${row.user_id}`
        })
        userOptions.value = Object.entries(users)
            .map(([id, name]) => ({ id: Number(id), name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }
}

// Persistance des filtres dans le localStorage (clé distincte de la page v1)
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage('request-statistics-filters')

const persistFilters = () => {
    saveToLocalStorage({
        center: selectedCenter.value,
        user: selectedUser.value,
        from: from.value,
        to: to.value,
    })
}

const restoreFilters = () => {
    const saved = loadFromLocalStorage()
    if (!saved) return

    selectedCenter.value = saved.center || null
    selectedUser.value = saved.user || null
    from.value = saved.from || null
    to.value = saved.to || null
}

// Sur l'onglet Appels, les stats de demandes ne sont pas rechargées à chaque filtre :
// elles le seront au retour sur l'onglet Demandes si les filtres ont changé entre-temps.
let requestsStale = false

const applyFilters = async () => {
    if (activeTab.value !== 'requests') {
        requestsStale = true
        return
    }

    requestsStale = false
    await stats.fetchAll({
        centerId: selectedCenter.value?.id || null,
        userId: selectedUser.value?.id || null,
        from: from.value || null,
        to: to.value || null,
    })
    buildOptionsIfNeeded()
}

let initialised = false

onMounted(async () => {
    restoreFilters()

    // Premier fetch SANS filtre centre/utilisateur : sert à construire les listes complètes
    await stats.fetchAll({ from: from.value, to: to.value })
    buildOptionsIfNeeded()

    if (!from.value) from.value = stats.userStatusBreakdown.value.from
    if (!to.value) to.value = stats.userStatusBreakdown.value.to

    if (selectedCenter.value || selectedUser.value) {
        await applyFilters()
    }

    await nextTick()
    initialised = true
    ready.value = true
})

watch([selectedCenter, selectedUser, from, to], () => {
    if (!initialised) return

    applyFilters()
    persistFilters()
})

// --- KPI ---
const kpis = computed(() => {
    const worksheets = stats.centerCreations.value.rows.reduce((sum, row) => sum + row.count, 0)
    const requests = stats.centerCreations.value.rows.reduce((sum, row) => sum + (row.requests_count || 0), 0)

    const responded = stats.centerCreations.value.rows.reduce((sum, row) => sum + (row.responded_count || 0), 0)
    const clients = stats.clientCreations.value.rows.reduce((sum, row) => sum + (row.count || 0), 0)

    let appointment = 0
    let won = 0
    let revenue = 0
    stats.conversions.value.rows.forEach((row) => {
        if (row.status === 'appointment_scheduled') appointment += row.count
        if (row.status === 'won') {
            won += row.count
            revenue += row.revenue || 0
        }
    })

    return { requests, worksheets, responded, clients, appointment, won, revenue, conversion: stats.cohort.value.total_rate || 0 }
})

// --- Tableau : conversions par utilisateur (gagnés nets, attribués) ---
const conversionsTable = computed(() => {
    const users = {}
    stats.conversions.value.rows.forEach((row) => {
        if (!users[row.user_id]) {
            users[row.user_id] = { id: row.user_id, name: row.user_name || `#${row.user_id}`, appointment: 0, won: 0 }
        }
        if (row.status === 'appointment_scheduled') users[row.user_id].appointment += row.count
        if (row.status === 'won') users[row.user_id].won += row.count
    })

    return Object.values(users).sort((a, b) => b.won - a.won)
})

// --- Tableau : fiches créées par centre ---
const creationsTable = computed(() => {
    const centers = {}
    stats.centerCreations.value.rows.forEach((row) => {
        if (!centers[row.center_id]) {
            centers[row.center_id] = { id: row.center_id, name: row.center_name || `#${row.center_id}`, total: 0 }
        }
        centers[row.center_id].total += row.count
    })

    return Object.values(centers).sort((a, b) => b.total - a.total)
})

// Cycle de vie d'une demande : ordre logique des statuts (par valeur, stable)
const STATUS_LIFECYCLE = [
    'pending',
    'responded',
    'to_follow_up',
    'parts_ordered',
    'appointment_scheduled',
    'appointment_ongoing',
    'won',
    'no_show',
    'lost',
    'ecu_in_dev',
    'rma',
    'duplicate',
    'closed',
]

// --- Tableau : tous les statuts par utilisateur (mode activité) ---
const statusBreakdown = computed(() => {
    const users = {}
    const labelByStatus = {}

    stats.userStatusBreakdown.value.rows.forEach((row) => {
        labelByStatus[row.status] = row.status_label
        if (!users[row.user_id]) {
            users[row.user_id] = { id: row.user_id, name: row.user_name || `#${row.user_id}`, byStatus: {} }
        }
        users[row.user_id].byStatus[row.status_label] = (users[row.user_id].byStatus[row.status_label] || 0) + row.count
    })

    // Colonnes ordonnées selon le cycle de vie ; les statuts inconnus à la fin
    const present = Object.keys(labelByStatus)
    const orderedStatuses = [
        ...STATUS_LIFECYCLE.filter((status) => present.includes(status)),
        ...present.filter((status) => !STATUS_LIFECYCLE.includes(status)),
    ]

    return {
        labels: orderedStatuses.map((status) => labelByStatus[status]),
        users: Object.values(users),
    }
})

// --- Graphique : Top N centres fiches (barres horizontales) ---
const topCentersChart = computed(() => {
    const ordered = [...creationsTable.value].slice(0, TOP_N)

    return {
        series: [{ name: 'Fiches créées', data: ordered.map((center) => center.total) }],
        options: {
            chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false, allowMouseWheelZoom: false } },
            colors: [COLORS.creations],
            plotOptions: { bar: { horizontal: true, borderRadius: 6, borderRadiusApplication: 'end', barHeight: '55%', dataLabels: { position: 'top' } } },
            dataLabels: { enabled: true, offsetX: 14, formatter: (val) => formatNumber(val), style: { colors: ['#495057'], fontWeight: 600, fontSize: '11px' } },
            grid: { borderColor: '#f1f1f1' },
            xaxis: { categories: ordered.map((center) => center.name) },
            legend: { show: false },
            noData: { text: 'Aucune donnée sur la période' },
        },
        height: Math.max(280, ordered.length * 42),
    }
})

// --- Graphique : prestations par centre (barres empilées, Top N centres) ---
const TOP_SERVICES = 6
const servicesByCenterChart = computed(() => {
    const rows = stats.servicesByCenter.value.rows || []

    // Total par centre et par prestation, pour ne garder que les plus significatifs.
    const parCentre = {}
    const nomCentre = {}
    const parPrestation = {}
    rows.forEach((row) => {
        parCentre[row.center_id] = (parCentre[row.center_id] || 0) + row.count
        // Un centre archivé remonte sans nom : on retombe sur son id plutôt que d'afficher « null ».
        nomCentre[row.center_id] = row.center_name || `#${row.center_id}`
        parPrestation[row.service] = (parPrestation[row.service] || 0) + row.count
    })

    const centres = Object.entries(parCentre).sort((a, b) => b[1] - a[1]).slice(0, TOP_N).map(([id]) => Number(id))
    const principales = Object.entries(parPrestation).sort((a, b) => b[1] - a[1]).slice(0, TOP_SERVICES).map(([nom]) => nom)
    const aUneQueue = Object.keys(parPrestation).length > principales.length

    // Au-delà du top, les prestations sont regroupées : sinon la légende devient illisible.
    const libelle = (service) => (principales.includes(service) ? service : 'Autres')
    const series = [...principales, ...(aUneQueue ? ['Autres'] : [])].map((nom) => ({
        name: nom,
        data: centres.map((centreId) => rows
            .filter((row) => row.center_id === centreId && libelle(row.service) === nom)
            .reduce((somme, row) => somme + row.count, 0)),
    }))

    return {
        series,
        options: {
            chart: { type: 'bar', stacked: true, toolbar: { show: false }, zoom: { enabled: false, allowMouseWheelZoom: false } },
            colors: series.map((serie, index) => serviceColor(serie.name, index)),
            plotOptions: { bar: { horizontal: true, borderRadius: 4, borderRadiusApplication: 'end', barHeight: '65%' } },
            dataLabels: { enabled: false },
            grid: { borderColor: '#f1f1f1' },
            xaxis: { categories: centres.map((id) => nomCentre[id]) },
            legend: { position: 'bottom' },
            tooltip: { y: { formatter: (val) => formatNumber(val) } },
            noData: { text: 'Aucune donnée sur la période' },
        },
        height: Math.max(320, centres.length * 44),
    }
})

// --- Tableau : conversions par centre (RDV programmés & Gagnés nets) ---
const conversionsByCentre = computed(() => {
    const centres = {}
    stats.conversions.value.rows.forEach((row) => {
        if (!centres[row.center_id]) {
            centres[row.center_id] = { id: row.center_id, name: row.center_name || `#${row.center_id}`, appointment: 0, won: 0 }
        }
        if (row.status === 'appointment_scheduled') centres[row.center_id].appointment += row.count
        if (row.status === 'won') centres[row.center_id].won += row.count
    })

    return Object.values(centres).sort((a, b) => b.won - a.won)
})

// --- Graphiques Top N RDV programmés & Gagnés (légende cliquable + tri auto) ---
const CONVERSION_METRICS = [
    { key: 'appointment', name: 'RDV programmés', color: COLORS.appointment },
    { key: 'won', name: 'Gagnés', color: COLORS.won },
]

const useConversionBarChart = (rowsRef) => {
    const visible = reactive({ appointment: true, won: true })

    const toggle = (key) => {
        const visibleCount = CONVERSION_METRICS.filter((metric) => visible[metric.key]).length
        if (visible[key] && visibleCount === 1) {
            return
        }
        visible[key] = !visible[key]
    }

    const chart = computed(() => {
        const visibleMetrics = CONVERSION_METRICS.filter((metric) => visible[metric.key])
        const sortKey = visibleMetrics.length === 1 ? visibleMetrics[0].key : 'won'
        const ordered = [...rowsRef.value].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0)).slice(0, TOP_N)

        return {
            series: visibleMetrics.map((metric) => ({ name: metric.name, data: ordered.map((row) => row[metric.key]) })),
            options: {
                chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false, allowMouseWheelZoom: false } },
                colors: visibleMetrics.map((metric) => metric.color),
                plotOptions: { bar: { horizontal: true, borderRadius: 6, borderRadiusApplication: 'end', barHeight: '55%', dataLabels: { position: 'top' } } },
                dataLabels: { enabled: true, offsetX: 14, formatter: (val) => formatNumber(val), style: { colors: ['#495057'], fontWeight: 600, fontSize: '11px' } },
                grid: { borderColor: '#f1f1f1' },
                xaxis: { categories: ordered.map((row) => row.name) },
                legend: { show: false },
                noData: { text: 'Aucune donnée sur la période' },
            },
            height: Math.max(280, ordered.length * 66),
        }
    })

    return { visible, toggle, chart }
}

const {
    visible: usersChartVisible,
    toggle: toggleUsersMetric,
    chart: topUsersChart,
} = useConversionBarChart(conversionsTable)

const {
    visible: centresChartVisible,
    toggle: toggleCentresMetric,
    chart: topCentresConversionChart,
} = useConversionBarChart(conversionsByCentre)

// Vue du graphe principal : 'results' (résultats du mois) ou 'cohort' (transformation)
const chartView = ref('results')

// --- Graphique "résultats du mois" : arrivées (mois de création) vs gagnées (mois du gain) ---
const monthlyResultsChart = computed(() => {
    const months = stats.cohort.value.months

    const createdByMonth = {}
    stats.cohort.value.rows.forEach((row) => {
        createdByMonth[row.period] = row.created
    })

    const wonByMonth = {}
    stats.conversions.value.rows.forEach((row) => {
        if (row.status === 'won') wonByMonth[row.period] = (wonByMonth[row.period] || 0) + row.count
    })

    return {
        series: [
            { name: 'Demandes arrivées', type: 'column', data: months.map((month) => createdByMonth[month] || 0) },
            { name: 'Demandes gagnées', type: 'column', data: months.map((month) => wonByMonth[month] || 0) },
        ],
        options: {
            chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false, allowMouseWheelZoom: false } },
            colors: [COLORS.requests, COLORS.won],
            stroke: { width: [0, 0] },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '65%' } },
            dataLabels: { enabled: false },
            grid: { borderColor: '#f1f1f1' },
            xaxis: { categories: months },
            legend: { show: false },
            tooltip: { shared: true, y: { formatter: (val) => formatNumber(val) } },
            noData: { text: 'Aucune donnée sur la période' },
        },
    }
})

// --- Graphique cohorte : par mois de création, demandes créées / gagnées + taux ---
const cohortChart = computed(() => {
    const rows = stats.cohort.value.rows

    return {
        series: [
            { name: 'Demandes créées', type: 'column', data: rows.map((row) => row.created) },
            { name: 'Gagnées à date', type: 'column', data: rows.map((row) => row.won) },
            { name: 'Taux de transformation', type: 'line', data: rows.map((row) => row.rate) },
        ],
        options: {
            chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false, allowMouseWheelZoom: false } },
            colors: [COLORS.requests, COLORS.won, COLORS.rate],
            stroke: { width: [0, 0, 3], curve: 'smooth' },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
            dataLabels: { enabled: false },
            grid: { borderColor: '#f1f1f1' },
            xaxis: { categories: rows.map((row) => row.period) },
            yaxis: [
                { title: { text: 'Demandes' }, seriesName: 'Demandes créées' },
                { show: false, seriesName: 'Demandes créées' },
                { opposite: true, title: { text: 'Taux (%)' }, seriesName: 'Taux de transformation', max: 100, min: 0 },
            ],
            legend: { show: false },
            tooltip: { shared: true, y: { formatter: (val, { seriesIndex }) => (seriesIndex === 2 ? formatPercent(val) : formatNumber(val)) } },
            noData: { text: 'Aucune donnée sur la période' },
        },
    }
})

// --- Classement mensuel des centres (daté par l'événement → stable et comparable) ---
const RANKING_METRICS = [
    { key: 'won', status: 'won', name: 'Gagnés' },
    { key: 'appointment', status: 'appointment_scheduled', name: 'RDV programmés' },
]
const rankingMetricKey = ref('won')

const monthlyRanking = computed(() => {
    const months = stats.conversions.value.months
    const status = RANKING_METRICS.find((metric) => metric.key === rankingMetricKey.value).status
    const centers = {}

    stats.conversions.value.rows.forEach((row) => {
        if (row.status !== status) return
        if (!centers[row.center_id]) {
            centers[row.center_id] = { id: row.center_id, name: row.center_name || `#${row.center_id}`, byMonth: {}, total: 0 }
        }
        centers[row.center_id].byMonth[row.period] = (centers[row.center_id].byMonth[row.period] || 0) + row.count
        centers[row.center_id].total += row.count
    })

    // Le mois courant est partiel : on n'affiche pas sa variation (elle paraîtrait
    // en chute alors que le mois n'est pas terminé).
    const now = new Date()
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    const rows = Object.values(centers).sort((a, b) => b.total - a.total)
    rows.forEach((row, index) => {
        row.rank = index + 1

        // Pour chaque mois : valeur + variation en % par rapport au mois précédent.
        // Le mois en cours est affiché aussi (marqué comme indicatif car partiel).
        row.cells = months.map((month, monthIndex) => {
            const value = row.byMonth[month] || 0
            let variation = null

            if (monthIndex > 0) {
                const prevValue = row.byMonth[months[monthIndex - 1]] || 0
                variation = prevValue === 0 ? null : Math.round(((value - prevValue) / prevValue) * 1000) / 10
            }

            return { month, value, variation, isCurrent: month === currentPeriod }
        })
    })

    return { months, rows }
})

const copyRankingTable = () => {
    const months = monthlyRanking.value.months
    copyHtmlTable(
        ['Rang', 'Centre', ...months, 'Total'],
        monthlyRanking.value.rows.map((row) => [row.rank, row.name, ...months.map((month) => row.byMonth[month] || 0), row.total]),
    )
}

// --- Tri du tableau de répartition des statuts ---
const sortColumn = ref('name')
const sortDir = ref('asc')

const setSort = (col) => {
    if (sortColumn.value === col) {
        sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
    } else {
        sortColumn.value = col
        sortDir.value = col === 'name' ? 'asc' : 'desc'
    }
}

const sortIconClass = (col) => {
    if (sortColumn.value !== col) return 'mdi-unfold-more-horizontal text-muted'
    return sortDir.value === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down'
}

const sortedBreakdownUsers = computed(() => {
    const dir = sortDir.value === 'asc' ? 1 : -1
    const col = sortColumn.value

    return [...statusBreakdown.value.users].sort((a, b) => {
        if (col === 'name') {
            return a.name.localeCompare(b.name) * dir
        }

        return ((a.byStatus[col] || 0) - (b.byStatus[col] || 0)) * dir
    })
})

const breakdownSearch = ref('')

const filteredBreakdownUsers = computed(() => {
    const term = breakdownSearch.value.trim().toLowerCase()
    if (!term) return sortedBreakdownUsers.value

    return sortedBreakdownUsers.value.filter((user) => user.name.toLowerCase().includes(term))
})

// --- Tri générique des tableaux de conversions ---
const useTableSort = (rowsRef, defaultColumn) => {
    const column = ref(defaultColumn)
    const direction = ref('desc')

    const setSort = (col) => {
        if (column.value === col) {
            direction.value = direction.value === 'desc' ? 'asc' : 'desc'
        } else {
            column.value = col
            direction.value = col === 'name' ? 'asc' : 'desc'
        }
    }

    const iconClass = (col) => {
        if (column.value !== col) return 'mdi-unfold-more-horizontal text-muted'

        return direction.value === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down'
    }

    const sorted = computed(() => {
        const dir = direction.value === 'asc' ? 1 : -1
        const col = column.value

        return [...rowsRef.value].sort((a, b) => {
            if (col === 'name') {
                return a.name.localeCompare(b.name) * dir
            }

            return ((a[col] || 0) - (b[col] || 0)) * dir
        })
    })

    return { setSort, iconClass, sorted }
}

const {
    setSort: setUsersSort,
    iconClass: usersSortIcon,
    sorted: sortedConversionsUsers,
} = useTableSort(conversionsTable, 'won')

const {
    setSort: setCentresSort,
    iconClass: centresSortIcon,
    sorted: sortedConversionsCentres,
} = useTableSort(conversionsByCentre, 'won')

// --- Copie d'un tableau au format HTML ---
const copyHtmlTable = async (headers, rows) => {
    const escape = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const thead = `<thead><tr>${headers.map((header) => `<th style="border:1px solid #ccc;padding:6px 12px;background:#f5f5f5;text-align:left;">${escape(header)}</th>`).join('')}</tr></thead>`
    const tbody = `<tbody>${rows.map((row) => `<tr>${row.map((cell, index) => `<td style="border:1px solid #ccc;padding:6px 12px;${index === 0 ? '' : 'text-align:center;'}">${escape(cell)}</td>`).join('')}</tr>`).join('')}</tbody>`
    const html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:14px;">${thead}${tbody}</table>`
    const text = [headers.join('\t'), ...rows.map((row) => row.join('\t'))].join('\n')

    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' }),
            }),
        ])
        $toast.success('Tableau copié dans le presse-papiers')
    } catch {
        try {
            await navigator.clipboard.writeText(text)
            $toast.success('Tableau copié dans le presse-papiers')
        } catch {
            $toast.error('Impossible de copier le tableau')
        }
    }
}

const copyCentresConversionTable = () => {
    copyHtmlTable(
        ['Centre', 'RDV programmés', 'Gagnés'],
        sortedConversionsCentres.value.map((centre) => [centre.name, centre.appointment, centre.won]),
    )
}

const copyCreationsTable = () => {
    copyHtmlTable(
        ['Centre', 'Fiches de travail créées'],
        creationsTable.value.map((centre) => [centre.name, centre.total]),
    )
}

const CHART_CARD_BODY_CHROME = 47
const creationPanelTableHeight = computed(() => `${topCentersChart.value.height + CHART_CARD_BODY_CHROME}px`)

// Convertit un mois "YYYY-MM" en date exacte JJ/MM/AAAA (1er du mois, ou dernier jour si fin de période)
const monthBoundaryDate = (period, isEnd) => {
    const [year, month] = period.split('-').map(Number)
    const day = isEnd ? new Date(year, month, 0).getDate() : 1
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
}

const periodLabel = computed(() => {
    // Onglets Appels et Objectifs : la période des filtres (celle des demandes n'y est pas rechargée)
    const { from: f, to: t } = activeTab.value === 'requests' ? stats.userStatusBreakdown.value : { from: from.value, to: to.value }
    if (!f || !t) return ''
    return `du ${monthBoundaryDate(f, false)} au ${monthBoundaryDate(t, true)}`
})

// Raccourcis de période : remplissent les champs "du"/"au" (au format YYYY-MM)
const periodPresets = computed(() => {
    const now = new Date()
    const ym = (year, month) => `${year}-${String(month).padStart(2, '0')}`
    const shift = (n) => {
        const d = new Date(now.getFullYear(), now.getMonth() - n, 1)
        return ym(d.getFullYear(), d.getMonth() + 1)
    }
    const current = ym(now.getFullYear(), now.getMonth() + 1)

    return [
        { key: 'current', label: 'Mois en cours', from: current, to: current },
        { key: 'last', label: 'Mois dernier', from: shift(1), to: shift(1) },
        { key: '3m', label: '3 derniers mois', from: shift(2), to: current },
        { key: '6m', label: '6 derniers mois', from: shift(5), to: current },
        { key: '12m', label: '12 derniers mois', from: shift(11), to: current },
        { key: 'ytd', label: 'Année en cours', from: ym(now.getFullYear(), 1), to: current },
    ]
})

const activePreset = computed(() => periodPresets.value.find((p) => p.from === from.value && p.to === to.value)?.key ?? null)

const applyPreset = (preset) => {
    from.value = preset.from
    to.value = preset.to
}

// Plage exacte correspondant à la sélection en cours (mise à jour en direct)
const selectedRangeLabel = computed(() => {
    if (!from.value || !to.value) return ''
    let [a, b] = [from.value, to.value]
    if (a > b) [a, b] = [b, a]
    return `du ${monthBoundaryDate(a, false)} au ${monthBoundaryDate(b, true)}`
})
</script>

<template>
    <div class="row">
        <div class="col-12">
            <!-- Mobile : le sélecteur d'onglets passe sous le titre, en pleine largeur -->
            <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                <div>
                    <h4 class="mb-0">Statistiques</h4>
                    <p class="text-muted mb-0" v-if="periodLabel">
                        Période : {{ periodLabel }}
                        <span v-if="activeTab === 'requests'" class="fs-12"> · <i class="mdi mdi-refresh align-middle"></i> données des demandes recalculées chaque nuit</span>
                    </p>
                </div>
                <div class="tab-switcher d-flex gap-1 bg-white border rounded-pill shadow-sm p-1">
                    <button type="button" class="btn btn-sm rounded-pill shadow-none px-3 text-nowrap" :class="activeTab === 'requests' ? 'btn-secondary' : 'btn-link text-muted text-decoration-none'" @click="activeTab = 'requests'">
                        <i class="mdi mdi-car-info align-middle me-1"></i> Demandes
                    </button>
                    <button type="button" class="btn btn-sm rounded-pill shadow-none px-3 text-nowrap" :class="activeTab === 'calls' ? 'btn-secondary' : 'btn-link text-muted text-decoration-none'" @click="activeTab = 'calls'" v-if="hasCalls">
                        <i class="mdi mdi-phone align-middle me-1"></i> Appels
                    </button>
                    <button type="button" class="btn btn-sm rounded-pill shadow-none px-3 text-nowrap" :class="activeTab === 'objectives' ? 'btn-secondary' : 'btn-link text-muted text-decoration-none'" @click="activeTab = 'objectives'">
                        <i class="mdi mdi-bullseye-arrow align-middle me-1"></i> Objectifs
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Filtres -->
    <div class="card">
        <div class="card-body">
            <div class="row g-3 align-items-end">
                <div :class="filterColClass">
                    <label class="form-label">Centre</label>
                    <multiselect
                        v-model="selectedCenter"
                        :options="centerOptions"
                        :multiple="false"
                        label="name"
                        track-by="id"
                        placeholder="Tous les centres"
                        select-label=""
                        selected-label="Sélectionné"
                        deselect-label="Retirer"
                        :class="{ 'ms-has-value': selectedCenter }"
                    >
                        <template #clear>
                            <div v-if="selectedCenter" class="multiselect__clear" @mousedown.prevent.stop="selectedCenter = null"></div>
                        </template>
                        <template #noResult>Aucun centre trouvé.</template>
                        <template #noOptions>Aucun centre disponible.</template>
                    </multiselect>
                </div>
                <!-- RingOver ne transmet pas l'agent : pas de filtre utilisateur sur les appels -->
                <div :class="filterColClass" v-if="activeTab === 'requests'">
                    <label class="form-label">Utilisateur</label>
                    <multiselect
                        v-model="selectedUser"
                        :options="userOptions"
                        :multiple="false"
                        label="name"
                        track-by="id"
                        placeholder="Tous les utilisateurs"
                        select-label=""
                        selected-label="Sélectionné"
                        deselect-label="Retirer"
                        :class="{ 'ms-has-value': selectedUser }"
                    >
                        <template #clear>
                            <div v-if="selectedUser" class="multiselect__clear" @mousedown.prevent.stop="selectedUser = null"></div>
                        </template>
                        <template #noResult>Aucun utilisateur trouvé.</template>
                        <template #noOptions>Aucun utilisateur disponible.</template>
                    </multiselect>
                </div>
                <FormMonthPicker
                    name="from"
                    label="Du (mois)"
                    :bs-class="filterColClass"
                    :data="from"
                    @update:field="from = $event"
                />
                <FormMonthPicker
                    name="to"
                    label="Au (mois)"
                    :bs-class="filterColClass"
                    :data="to"
                    @update:field="to = $event"
                />
                <div class="col-12">
                    <div class="d-flex align-items-center flex-wrap gap-2">
                        <span class="text-muted small me-1"><i class="mdi mdi-clock-fast align-middle me-1"></i> Raccourcis :</span>
                        <button
                            v-for="preset in periodPresets"
                            :key="preset.key"
                            type="button"
                            class="btn btn-sm"
                            :class="activePreset === preset.key ? 'btn-secondary shadow-none' : 'btn-soft-secondary shadow-none'"
                            @click="applyPreset(preset)"
                        >
                            {{ preset.label }}
                        </button>
                    </div>
                </div>
                <div class="col-12" v-if="selectedRangeLabel">
                    <span class="text-muted small">
                        <i class="mdi mdi-calendar-range align-middle me-1"></i>
                        Période analysée : <strong>{{ selectedRangeLabel }}</strong> (du 1<sup>er</sup> jour du mois de début au dernier jour du mois de fin).
                    </span>
                </div>
            </div>
        </div>
    </div>

    <template v-if="activeTab === 'requests'">
    <!-- Contenu : structure toujours rendue, placeholders uniquement sur les zones de données -->
    <!-- KPI -->
        <div class="row">
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Demandes clients créées</p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-dark">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.requests) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-dark-subtle text-dark rounded-circle fs-2">
                                        <i class="mdi mdi-car-info"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">
                                    Contacts créés&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CONTACTS_HELP"></i>
                                </p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-dark">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.clients) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-dark-subtle text-dark rounded-circle fs-2">
                                        <i class="mdi mdi-account-multiple-plus"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">
                                    Répondu&nbsp;<i class="mdi mdi-information-outline" v-tooltip="RESPONDED_HELP"></i>
                                </p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-dark">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.responded) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-dark-subtle text-dark rounded-circle fs-2">
                                        <i class="mdi mdi-message-reply-text"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">RDV programmés</p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-warning">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.appointment) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-2">
                                        <i class="mdi mdi-calendar-clock"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">
                                    Gagnés (nets)&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'Demandes encore gagnées aujourd\'hui, dédupliquées'"></i>
                                </p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-success">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.won) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-success-subtle text-success rounded-circle fs-2">
                                        <i class="mdi mdi-trophy-outline"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">
                                    Chiffre d'affaires&nbsp;<i class="mdi mdi-information-outline" v-tooltip="REVENUE_HELP"></i>
                                </p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-dark">
                                    <span v-if="!stats.fetching.value">{{ formatAmount(kpis.revenue) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-dark-subtle text-dark rounded-circle fs-2">
                                        <i class="mdi mdi-cash-multiple"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Fiches de travail créées</p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold text-info">
                                    <span v-if="!stats.fetching.value">{{ formatNumber(kpis.worksheets) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-info-subtle text-info rounded-circle fs-2">
                                        <i class="mdi mdi-file-document-multiple-outline"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">
                                    Taux de transformation&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'Parmi les demandes arrivées sur la période, part qui a fini gagnée à ce jour. Les mois récents tirent ce taux vers le bas (demandes pas encore transformées).'"></i>
                                </p>
                                <h2 class="mt-2 mb-0 ff-secondary fw-semibold">
                                    <span v-if="!stats.fetching.value">{{ formatPercent(kpis.conversion) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-5"></span></span>
                                </h2>
                            </div>
                            <div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-secondary-subtle text-secondary rounded-circle fs-2">
                                        <i class="mdi mdi-percent-outline"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Graphe principal : Résultats du mois OU Transformation (cohorte), au choix -->
        <div class="card">
            <div class="card-header align-items-center d-flex flex-wrap gap-2">
                <h4 class="card-title mb-0 flex-grow-1" v-if="chartView === 'results'">
                    Résultats par mois&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'Résultat commercial de chaque mois : demandes arrivées (au mois de création) et demandes gagnées (au mois où elles ont été gagnées). Reflète ce qui s\'est passé dans le mois.'"></i>
                </h4>
                <h4 class="card-title mb-0 flex-grow-1" v-else>
                    Transformation des demandes par mois d'arrivée&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'On regroupe les demandes par mois de création, et on regarde combien ont fini gagnées (quel que soit le moment où elles ont été gagnées). Mesure la qualité des leads.'"></i>
                </h4>
                <span v-if="chartView === 'cohort'" class="badge bg-light text-body fs-12">
                    Taux global : <strong class="ms-1">{{ formatPercent(stats.cohort.value.total_rate || 0) }}</strong>
                </span>
                <div class="btn-group btn-group-sm flex-shrink-0" role="group">
                    <button type="button" class="btn" :class="chartView === 'results' ? 'btn-secondary shadow-none' : 'btn-soft-secondary shadow-none'" @click="chartView = 'results'">
                        Résultats du mois
                    </button>
                    <button type="button" class="btn" :class="chartView === 'cohort' ? 'btn-secondary shadow-none' : 'btn-soft-secondary shadow-none'" @click="chartView = 'cohort'">
                        Transformation
                    </button>
                </div>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="stats.fetching.value" variant="vertical" :height="300" />
                <template v-else>
                    <StatChart v-if="chartView === 'results'" type="line" :height="350" :series="monthlyResultsChart.series" :options="monthlyResultsChart.options" />
                    <StatChart v-else type="line" :height="350" :series="cohortChart.series" :options="cohortChart.options" />

                    <div class="d-flex justify-content-center flex-wrap gap-3 mt-1" v-if="chartView === 'results'">
                        <span class="d-inline-flex align-items-center small">
                            <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: COLORS.requests }"></span>
                            Demandes arrivées (mois de création)
                        </span>
                        <span class="d-inline-flex align-items-center small">
                            <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: COLORS.won }"></span>
                            Demandes gagnées (mois du gain)
                        </span>
                    </div>
                    <template v-else>
                        <div class="d-flex justify-content-center flex-wrap gap-3 mt-1">
                            <span class="d-inline-flex align-items-center small">
                                <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: COLORS.requests }"></span>
                                Demandes créées
                            </span>
                            <span class="d-inline-flex align-items-center small">
                                <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: COLORS.won }"></span>
                                Gagnées à date
                            </span>
                            <span class="d-inline-flex align-items-center small">
                                <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: COLORS.rate }"></span>
                                Taux de transformation
                            </span>
                        </div>
                        <p class="text-muted small mb-0 mt-2">
                            <i class="mdi mdi-alert-circle-outline align-middle me-1"></i>
                            Les mois récents apparaissent normalement plus bas : les demandes qui viennent d'arriver n'ont pas
                            encore eu le temps d'être transformées.
                        </p>
                    </template>
                </template>
            </div>
        </div>

        <!-- Classement mensuel des centres (comparable mois après mois) -->
        <div class="card">
            <div class="card-header align-items-center d-flex flex-wrap gap-2">
                <h4 class="card-title mb-0 flex-grow-1">
                    Classement mensuel des centres&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'Daté par le mois où la demande a été gagnée (ou le mois du RDV) : stable et comparable d\'un mois à l\'autre.'"></i>
                </h4>
                <div class="btn-group btn-group-sm flex-shrink-0" role="group">
                    <button
                        v-for="metric in RANKING_METRICS"
                        :key="metric.key"
                        type="button"
                        class="btn"
                        :class="rankingMetricKey === metric.key ? 'btn-secondary shadow-none' : 'btn-soft-secondary shadow-none'"
                        @click="rankingMetricKey = metric.key"
                    >
                        {{ metric.name }}
                    </button>
                </div>
                <button type="button" class="btn btn-sm btn-soft-secondary shadow-none flex-shrink-0" @click="copyRankingTable">
                    <i class="ri-file-copy-line align-bottom"></i> Copier
                </button>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="420" />
                <div v-else class="table-responsive table-card" style="max-height: 520px;">
                    <table class="table table-bordered table-hover table-nowrap align-middle mb-0 ranking-table">
                        <colgroup>
                            <col class="col-rank" />
                            <col class="col-centre" />
                            <template v-for="month in monthlyRanking.months" :key="`${month}-col`">
                                <col class="col-data" />
                                <col class="col-data" />
                            </template>
                            <col class="col-data" />
                        </colgroup>
                        <thead class="table-light position-sticky top-0">
                            <tr class="text-muted text-center">
                                <th scope="col" rowspan="2" class="align-middle">#</th>
                                <th scope="col" rowspan="2" class="align-middle text-start">Centre</th>
                                <th scope="col" class="text-center" colspan="2" v-for="month in monthlyRanking.months" :key="month">{{ month }}</th>
                                <th scope="col" rowspan="2" class="align-middle">Total</th>
                            </tr>
                            <tr class="text-muted text-center">
                                <template v-for="month in monthlyRanking.months" :key="`${month}-sub`">
                                    <th scope="col" class="fw-normal">Nb</th>
                                    <th scope="col" class="fw-normal">Var.</th>
                                </template>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="center in monthlyRanking.rows" :key="center.id">
                                <td class="text-center"><span class="badge bg-light text-body">{{ center.rank }}</span></td>
                                <td class="fw-medium centre-cell">{{ center.name }}</td>
                                <template v-for="cell in center.cells" :key="cell.month">
                                    <td class="text-center">{{ formatNumber(cell.value) }}</td>
                                    <td class="text-center">
                                        <span
                                            v-if="cell.variation !== null"
                                            class="badge variation-badge fs-12"
                                            :class="[cell.variation > 0 ? 'bg-success-subtle text-success' : cell.variation < 0 ? 'bg-danger-subtle text-danger' : 'bg-light text-muted', { 'variation-current': cell.isCurrent }]"
                                            v-tooltip="cell.isCurrent ? 'Mois en cours (partiel) : variation indicative' : ''"
                                        >{{ cell.variation > 0 ? '+' : '' }}{{ formatPercent(cell.variation) }}</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                </template>
                                <td class="text-center fw-semibold">{{ formatNumber(center.total) }}</td>
                            </tr>
                            <tr v-if="monthlyRanking.rows.length === 0">
                                <td :colspan="monthlyRanking.months.length * 2 + 3" class="text-center text-muted py-4">Aucune donnée sur la période</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Top N (barres horizontales) -->
        <div class="row">
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            Top 10 utilisateurs — RDV programmés &amp; Gagnés&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CONVERSION_HELP"></i>
                        </h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="topUsersChart.height" />
                        <StatChart v-else type="bar" :height="topUsersChart.height" :series="topUsersChart.series" :options="topUsersChart.options" />
                        <div class="d-flex justify-content-center flex-wrap gap-3 mt-2">
                            <span
                                v-for="metric in CONVERSION_METRICS"
                                :key="metric.key"
                                role="button"
                                class="user-select-none d-inline-flex align-items-center small"
                                :class="{ 'opacity-50 text-decoration-line-through': !usersChartVisible[metric.key] }"
                                @click="toggleUsersMetric(metric.key)"
                            >
                                <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: metric.color }"></span>
                                {{ metric.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            Top 10 centres — RDV programmés &amp; Gagnés&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CONVERSION_HELP"></i>
                        </h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="topCentresConversionChart.height" />
                        <StatChart v-else type="bar" :height="topCentresConversionChart.height" :series="topCentresConversionChart.series" :options="topCentresConversionChart.options" />
                        <div class="d-flex justify-content-center flex-wrap gap-3 mt-2">
                            <span
                                v-for="metric in CONVERSION_METRICS"
                                :key="metric.key"
                                role="button"
                                class="user-select-none d-inline-flex align-items-center small"
                                :class="{ 'opacity-50 text-decoration-line-through': !centresChartVisible[metric.key] }"
                                @click="toggleCentresMetric(metric.key)"
                            >
                                <span class="d-inline-block rounded-circle me-1" :style="{ width: '10px', height: '10px', backgroundColor: metric.color }"></span>
                                {{ metric.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <!-- Conversions par utilisateur -->
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            RDV programmés &amp; Gagnés par utilisateur&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CONVERSION_HELP"></i>
                        </h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="360" />
                        <div v-else class="table-responsive table-card" style="max-height: 420px;">
                            <table class="table table-borderless table-hover table-nowrap align-middle mb-0">
                                <thead class="table-light position-sticky top-0">
                                    <tr class="text-muted">
                                        <th scope="col" role="button" class="user-select-none" @click="setUsersSort('name')">
                                            Utilisateur <i class="mdi align-middle" :class="usersSortIcon('name')"></i>
                                        </th>
                                        <th scope="col" class="text-center user-select-none" role="button" @click="setUsersSort('appointment')">
                                            RDV programmés <i class="mdi align-middle" :class="usersSortIcon('appointment')"></i>
                                        </th>
                                        <th scope="col" class="text-center user-select-none" role="button" @click="setUsersSort('won')">
                                            Gagnés <i class="mdi align-middle" :class="usersSortIcon('won')"></i>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="user in sortedConversionsUsers" :key="user.id">
                                        <td class="fw-medium">{{ user.name }}</td>
                                        <td class="text-center">{{ formatNumber(user.appointment) }}</td>
                                        <td class="text-center">
                                            <span class="badge bg-success-subtle text-success">{{ formatNumber(user.won) }}</span>
                                        </td>
                                    </tr>
                                    <tr v-if="sortedConversionsUsers.length === 0">
                                        <td colspan="3" class="text-center text-muted py-4">Aucune donnée sur la période</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Conversions par centre -->
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            RDV programmés &amp; Gagnés par centre&nbsp;<i class="mdi mdi-information-outline" v-tooltip="CONVERSION_HELP"></i>
                        </h4>
                        <button type="button" class="btn btn-sm btn-soft-secondary shadow-none flex-shrink-0" @click="copyCentresConversionTable">
                            <i class="ri-file-copy-line align-bottom"></i> Copier
                        </button>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="360" />
                        <div v-else class="table-responsive table-card" style="max-height: 420px;">
                            <table class="table table-borderless table-hover table-nowrap align-middle mb-0">
                                <thead class="table-light position-sticky top-0">
                                    <tr class="text-muted">
                                        <th scope="col" role="button" class="user-select-none" @click="setCentresSort('name')">
                                            Centre <i class="mdi align-middle" :class="centresSortIcon('name')"></i>
                                        </th>
                                        <th scope="col" class="text-center user-select-none" role="button" @click="setCentresSort('appointment')">
                                            RDV programmés <i class="mdi align-middle" :class="centresSortIcon('appointment')"></i>
                                        </th>
                                        <th scope="col" class="text-center user-select-none" role="button" @click="setCentresSort('won')">
                                            Gagnés <i class="mdi align-middle" :class="centresSortIcon('won')"></i>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="centre in sortedConversionsCentres" :key="centre.id">
                                        <td class="fw-medium">{{ centre.name }}</td>
                                        <td class="text-center">{{ formatNumber(centre.appointment) }}</td>
                                        <td class="text-center">
                                            <span class="badge bg-success-subtle text-success">{{ formatNumber(centre.won) }}</span>
                                        </td>
                                    </tr>
                                    <tr v-if="sortedConversionsCentres.length === 0">
                                        <td colspan="3" class="text-center text-muted py-4">Aucune donnée sur la période</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Création de fiches : graphique + tableau côte à côte -->
        <div class="row">
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            Top 10 centres — fiches de travail créées&nbsp;<i class="mdi mdi-information-outline" v-tooltip="FICHES_HELP"></i>
                        </h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="topCentersChart.height" />
                        <StatChart v-else type="bar" :height="topCentersChart.height" :series="topCentersChart.series" :options="topCentersChart.options" />
                    </div>
                </div>
            </div>
            <div class="col-xl-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            Fiches de travail créées par centre&nbsp;<i class="mdi mdi-information-outline" v-tooltip="FICHES_HELP"></i>
                        </h4>
                        <button type="button" class="btn btn-sm btn-soft-secondary shadow-none flex-shrink-0" @click="copyCreationsTable">
                            <i class="ri-file-copy-line align-bottom"></i> Copier
                        </button>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="topCentersChart.height" />
                        <div v-else class="table-responsive table-card" :style="{ height: creationPanelTableHeight, overflowY: 'auto' }">
                            <table class="table table-borderless table-hover table-nowrap align-middle mb-0">
                                <thead class="table-light position-sticky top-0">
                                    <tr class="text-muted">
                                        <th scope="col">Centre</th>
                                        <th scope="col" class="text-center">Fiches de travail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="center in creationsTable" :key="center.id">
                                        <td class="fw-medium">{{ center.name }}</td>
                                        <td class="text-center">
                                            <span class="badge bg-info-subtle text-info">{{ formatNumber(center.total) }}</span>
                                        </td>
                                    </tr>
                                    <tr v-if="creationsTable.length === 0">
                                        <td colspan="2" class="text-center text-muted py-4">Aucune donnée sur la période</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Prestations par centre -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            Prestations par centre&nbsp;<i class="mdi mdi-information-outline" v-tooltip="SERVICES_HELP"></i>
                        </h4>
                    </div>
                    <div class="card-body">
                        <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="servicesByCenterChart.height" />
                        <StatChart v-else type="bar" :height="servicesByCenterChart.height" :series="servicesByCenterChart.series" :options="servicesByCenterChart.options" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Répartition de tous les statuts par utilisateur (mode activité) -->
        <div class="card">
            <div class="card-header align-items-center d-flex">
                <h4 class="card-title mb-0 flex-grow-1">
                    Répartition de tous les statuts par utilisateur&nbsp;<i class="mdi mdi-information-outline" v-tooltip="'Basé sur l\'activité : qui a positionné chaque statut (transitions)'"></i>
                </h4>
                <div class="flex-shrink-0">
                    <div class="search-box">
                        <input v-model="breakdownSearch" type="text" class="form-control form-control-sm" placeholder="Rechercher un utilisateur..." />
                        <i class="ri-search-line search-icon"></i>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="stats.fetching.value" variant="horizontal" :height="420" />
                <div v-else class="table-responsive table-card" style="max-height: 520px;">
                    <table class="table table-bordered table-hover table-nowrap align-middle mb-0 breakdown-table">
                        <thead class="table-light position-sticky top-0">
                            <tr class="text-muted">
                                <th scope="col" role="button" class="user-select-none" @click="setSort('name')">
                                    Utilisateur <i class="mdi align-middle" :class="sortIconClass('name')"></i>
                                </th>
                                <th scope="col" class="text-center user-select-none" role="button" v-for="label in statusBreakdown.labels" :key="label" @click="setSort(label)">
                                    {{ label }} <i class="mdi align-middle" :class="sortIconClass(label)"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="user in filteredBreakdownUsers" :key="user.id">
                                <td class="fw-medium">{{ user.name }}</td>
                                <td class="text-center" v-for="label in statusBreakdown.labels" :key="label">
                                    {{ formatNumber(user.byStatus[label] || 0) }}
                                </td>
                            </tr>
                            <tr v-if="filteredBreakdownUsers.length === 0">
                                <td :colspan="statusBreakdown.labels.length + 1" class="text-center text-muted py-4">
                                    Aucun utilisateur ne correspond
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </template>

    <!-- Onglets Appels et Objectifs : mêmes filtres de centre et de période -->
    <RevenueStatistics
        v-else-if="ready && activeTab === 'objectives'"
        :center-id="selectedCenter?.id || null"
        :from="from"
        :to="to"
    />
    <CallStatistics
        v-else-if="ready"
        :center-id="selectedCenter?.id || null"
        :from="from"
        :to="to"
    />
</template>

<style scoped>
/* Sélecteur d'onglets : taille naturelle sur ordinateur, trois boutons égaux sur mobile */
@media (max-width: 767.98px) {
    .tab-switcher {
        width: 100%;
    }

    .tab-switcher .btn {
        flex: 1 1 0;
    }
}

th[role="button"] {
    cursor: pointer;
    white-space: nowrap;
}

/* Classement mensuel : largeurs de colonnes figées et identiques entre les mois */
.ranking-table {
    table-layout: fixed;
}

.ranking-table .col-rank {
    width: 48px;
}

.ranking-table .col-centre {
    width: 170px;
}

.ranking-table .col-data {
    width: 82px;
}

/* Le nom du centre peut revenir à la ligne plutôt que déborder de sa colonne fixe */
.ranking-table .centre-cell {
    white-space: normal;
}

/* L'en-tête (sticky) doit passer AU-DESSUS des colonnes figées du corps (z-index 2) */
.ranking-table thead,
.breakdown-table thead {
    z-index: 3;
}

/* Classement : on fige le rang (#) et le centre au scroll horizontal */
.ranking-table thead tr:first-child th:nth-child(1),
.ranking-table tbody td:nth-child(1) {
    position: sticky;
    left: 0;
    z-index: 2;
    background-color: var(--vz-card-bg, #fff);
}

.ranking-table thead tr:first-child th:nth-child(2),
.ranking-table tbody td:nth-child(2) {
    position: sticky;
    left: 48px;
    z-index: 2;
    background-color: var(--vz-card-bg, #fff);
}

/* Coin haut-gauche (# et Centre) : figé sur les DEUX axes (vertical + horizontal) */
.ranking-table thead tr:first-child th:nth-child(1),
.ranking-table thead tr:first-child th:nth-child(2) {
    top: 0;
    z-index: 4;
    background-color: var(--vz-light, #f3f6f9);
}

/* Répartition des statuts : on fige le nom d'utilisateur au scroll horizontal */
.breakdown-table th:first-child,
.breakdown-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 2;
    background-color: var(--vz-card-bg, #fff);
}

.breakdown-table thead th:first-child {
    top: 0;
    z-index: 4;
    background-color: var(--vz-light, #f3f6f9);
}

/* Badge de variation : texte parfaitement centré */
.variation-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    padding: 0.28em 0.5em;
    line-height: 1;
}

/* Mois en cours : variation indicative (partielle) → style discret pointillé */
.variation-badge.variation-current {
    border: 1px dashed currentColor;
    opacity: 0.75;
}

/* Croix de désélection (style global) à la place de la flèche, masquée quand une valeur est choisie */
.multiselect__clear {
    right: 10px;
}

:deep(.ms-has-value .multiselect__select) {
    display: none;
}
</style>
